import { supabase } from '../lib/supabaseClient';

interface MessageChannel {
  type: 'whatsapp' | 'messenger' | 'sms' | 'telegram';
  id: string;
  name: string;
  enabled: boolean;
}

interface ExternalMessage {
  channel: MessageChannel;
  from: string;
  to: string;
  content: string;
  mediaUrl?: string;
  timestamp: Date;
}

export class ChatbotMessagingService {
  // Konfiguracja kanałów
  private channels: MessageChannel[] = [
    {
      type: 'whatsapp',
      id: 'whatsapp_business',
      name: 'WhatsApp Business',
      enabled: false // Wymaga konfiguracji API
    },
    {
      type: 'messenger',
      id: 'facebook_messenger',
      name: 'Facebook Messenger',
      enabled: false // Wymaga konfiguracji API
    },
    {
      type: 'sms',
      id: 'sms_gateway',
      name: 'SMS Gateway',
      enabled: false // Wymaga konfiguracji API
    },
    {
      type: 'telegram',
      id: 'telegram_bot',
      name: 'Telegram Bot',
      enabled: false // Wymaga konfiguracji API
    }
  ];

  // Pobierz dostępne kanały
  getAvailableChannels(): MessageChannel[] {
    return this.channels.filter(channel => channel.enabled);
  }

  // Wyślij wiadomość przez zewnętrzny kanał
  async sendMessage(channel: MessageChannel['type'], to: string, content: string): Promise<boolean> {
    try {
      switch (channel) {
        case 'whatsapp':
          return await this.sendWhatsAppMessage(to, content);
        case 'messenger':
          return await this.sendMessengerMessage(to, content);
        case 'sms':
          return await this.sendSMSMessage(to, content);
        case 'telegram':
          return await this.sendTelegramMessage(to, content);
        default:
          throw new Error(`Nieobsługiwany kanał: ${channel}`);
      }
    } catch (error) {
      console.error(`Błąd wysyłania wiadomości ${channel}:`, error);
      return false;
    }
  }

  // WhatsApp Business API (wymaga konfiguracji)
  private async sendWhatsAppMessage(to: string, content: string): Promise<boolean> {
    // Przykład integracji z WhatsApp Business API
    if (!process.env.VITE_WHATSAPP_API_KEY) {
      console.warn('WhatsApp API nie jest skonfigurowane');
      return false;
    }

    try {
      const response = await fetch('https://api.whatsapp.com/send', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.VITE_WHATSAPP_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to,
          type: 'text',
          text: { body: content }
        })
      });

      return response.ok;
    } catch (error) {
      console.error('WhatsApp API error:', error);
      return false;
    }
  }

  // Facebook Messenger API (wymaga konfiguracji)
  private async sendMessengerMessage(to: string, content: string): Promise<boolean> {
    if (!process.env.VITE_MESSENGER_PAGE_TOKEN) {
      console.warn('Messenger API nie jest skonfigurowane');
      return false;
    }

    try {
      const response = await fetch(
        `https://graph.facebook.com/v18.0/me/messages?access_token=${process.env.VITE_MESSENGER_PAGE_TOKEN}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            recipient: { id: to },
            message: { text: content }
          })
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Messenger API error:', error);
      return false;
    }
  }

  // SMS Gateway (wymaga konfiguracji)
  private async sendSMSMessage(to: string, content: string): Promise<boolean> {
    if (!process.env.VITE_SMS_API_KEY) {
      console.warn('SMS API nie jest skonfigurowane');
      return false;
    }

    // Przykład z SMSAPI.pl
    try {
      const response = await fetch('https://api.smsapi.pl/sms.do', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${process.env.VITE_SMS_API_KEY}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          to,
          message: content,
          from: 'PlexiSystem',
          encoding: 'utf-8'
        })
      });

      return response.ok;
    } catch (error) {
      console.error('SMS API error:', error);
      return false;
    }
  }

  // Telegram Bot API
  private async sendTelegramMessage(chatId: string, content: string): Promise<boolean> {
    if (!process.env.VITE_TELEGRAM_BOT_TOKEN) {
      console.warn('Telegram Bot API nie jest skonfigurowane');
      return false;
    }

    try {
      const response = await fetch(
        `https://api.telegram.org/bot${process.env.VITE_TELEGRAM_BOT_TOKEN}/sendMessage`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            chat_id: chatId,
            text: content,
            parse_mode: 'Markdown'
          })
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Telegram API error:', error);
      return false;
    }
  }

  // Zapisz wiadomość w bazie
  async saveExternalMessage(message: ExternalMessage): Promise<void> {
    try {
      await supabase
        .from('external_messages')
        .insert({
          channel: message.channel.type,
          from_number: message.from,
          to_number: message.to,
          content: message.content,
          media_url: message.mediaUrl,
          timestamp: message.timestamp,
          status: 'received'
        });
    } catch (error) {
      console.error('Błąd zapisu wiadomości:', error);
    }
  }

  // Webhook dla przychodzących wiadomości
  async handleIncomingMessage(channel: MessageChannel['type'], data: any): Promise<string> {
    let message: ExternalMessage;

    // Parsuj wiadomość w zależności od kanału
    switch (channel) {
      case 'whatsapp':
        message = {
          channel: this.channels.find(c => c.type === 'whatsapp')!,
          from: data.from,
          to: data.to,
          content: data.text?.body || '',
          mediaUrl: data.image?.url || data.document?.url,
          timestamp: new Date(data.timestamp * 1000)
        };
        break;

      case 'messenger':
        message = {
          channel: this.channels.find(c => c.type === 'messenger')!,
          from: data.sender.id,
          to: data.recipient.id,
          content: data.message.text || '',
          mediaUrl: data.message.attachments?.[0]?.payload?.url,
          timestamp: new Date(data.timestamp)
        };
        break;

      case 'telegram':
        message = {
          channel: this.channels.find(c => c.type === 'telegram')!,
          from: data.message.from.id.toString(),
          to: 'bot',
          content: data.message.text || '',
          mediaUrl: data.message.photo?.[0]?.file_id,
          timestamp: new Date(data.message.date * 1000)
        };
        break;

      default:
        throw new Error(`Nieobsługiwany kanał: ${channel}`);
    }

    // Zapisz wiadomość
    await this.saveExternalMessage(message);

    // Przetwórz przez czatbota i zwróć odpowiedź
    // Tu możesz połączyć z claudeServiceV2
    return 'Dziękuję za wiadomość! Nasz konsultant wkrótce odpowie.';
  }

  // Generuj instrukcje konfiguracji dla użytkownika
  getSetupInstructions(channel: MessageChannel['type']): string {
    const instructions: Record<MessageChannel['type'], string> = {
      whatsapp: `
## Konfiguracja WhatsApp Business API

1. **Załóż konto WhatsApp Business**
   - Przejdź na: https://business.whatsapp.com/
   - Zarejestruj firmę

2. **Uzyskaj dostęp do API**
   - Złóż wniosek o WhatsApp Business API
   - Poczekaj na weryfikację (1-2 dni)

3. **Skonfiguruj webhook**
   - URL: ${window.location.origin}/api/webhooks/whatsapp
   - Token weryfikacji: [wygeneruj w panelu]

4. **Dodaj klucz API do .env**
   \`\`\`
   VITE_WHATSAPP_API_KEY=your_api_key_here
   VITE_WHATSAPP_PHONE_ID=your_phone_id_here
   \`\`\`
`,
      messenger: `
## Konfiguracja Facebook Messenger

1. **Utwórz aplikację Facebook**
   - Przejdź na: https://developers.facebook.com/
   - Utwórz nową aplikację

2. **Dodaj Messenger**
   - W panelu aplikacji dodaj produkt "Messenger"
   - Wygeneruj token strony

3. **Skonfiguruj webhook**
   - URL: ${window.location.origin}/api/webhooks/messenger
   - Verify Token: [ustaw własny]

4. **Dodaj dane do .env**
   \`\`\`
   VITE_MESSENGER_PAGE_TOKEN=your_page_token_here
   VITE_MESSENGER_APP_SECRET=your_app_secret_here
   \`\`\`
`,
      sms: `
## Konfiguracja SMS Gateway

1. **Wybierz dostawcę SMS**
   - SMSAPI.pl
   - SMSLabs.pl
   - Twilio

2. **Załóż konto i doładuj**
   - Zarejestruj się u wybranego dostawcy
   - Doładuj konto (ceny: 0.05-0.15 PLN/SMS)

3. **Pobierz klucz API**
   - Wygeneruj w panelu dostawcy

4. **Dodaj do .env**
   \`\`\`
   VITE_SMS_API_KEY=your_sms_api_key_here
   VITE_SMS_FROM=PlexiSystem
   \`\`\`
`,
      telegram: `
## Konfiguracja Telegram Bot

1. **Utwórz bota**
   - Napisz do @BotFather na Telegramie
   - Użyj komendy /newbot
   - Nadaj nazwę i username

2. **Zapisz token**
   - BotFather poda ci token bota

3. **Ustaw webhook**
   - URL: ${window.location.origin}/api/webhooks/telegram

4. **Dodaj do .env**
   \`\`\`
   VITE_TELEGRAM_BOT_TOKEN=your_bot_token_here
   \`\`\`
`
    };

    return instructions[channel] || 'Brak instrukcji dla tego kanału.';
  }
}

export const chatbotMessaging = new ChatbotMessagingService();
